import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { ArrowLeft, Eye, EyeOff, Loader2, Pencil, ScanFace, ShieldAlert, Trash2, X } from "lucide-react";
import { senhaForte } from "./CadastrarSenhaModal";
import MobileFacial from "./MobileFacial";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";

const glassCard = {
  background: "rgba(40, 42, 50, 0.6)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(141, 144, 160, 0.5)",
  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
};
const inputClass = "w-full rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70";
const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)", color: "#e1e2ed", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
  colorScheme: "dark",
};

function fmtDataBR(iso) {
  if (!iso) return "";
  const d = new Date(String(iso).slice(0, 10) + "T12:00:00");
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function fmtFone(ddi, cel) {
  if (!cel) return "";
  const d = String(cel).replace(/\D/g, "");
  if (d.length === 11) return `${ddi || "+55"} (${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${ddi || "+55"} (${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `${ddi || ""} ${cel}`.trim();
}

function fmtCpf(cpf) {
  const d = String(cpf || "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf || "";
}

function InfoRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <span className="text-[11px] font-semibold uppercase tracking-wider shrink-0" style={{ color: "#8d90a0" }}>{label}</span>
      <span className="text-xs font-medium text-right" style={{ color: value ? "#e1e2ed" : "#5a5d6d" }} data-testid={testid}>
        {value || "—"}
      </span>
    </div>
  );
}

function EditField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>{label}</label>
      <input data-testid={testid} className={inputClass} style={inputStyle} {...props} />
    </div>
  );
}

export default function MobilePerfil({ origem, eql, cpf, onClose }) {
  const [perfil, setPerfil] = useState(null);
  const [modo, setModo] = useState("view"); // view | edit
  const [form, setForm] = useState(null);
  const [senhaForm, setSenhaForm] = useState({ nova: "", confirma: "" });
  const [showN, setShowN] = useState(false);
  const [showC, setShowC] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [facial, setFacial] = useState(null);
  const [facialOpen, setFacialOpen] = useState(false);

  const carregarFacial = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/facial/status?origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`);
      const d = await r.json();
      if (d.ok) setFacial(d);
    } catch { /* silencioso */ }
  }, [origem, eql, cpf]);

  const qs = `origem=${encodeURIComponent(origem)}&eql=${encodeURIComponent(eql)}&cpf=${cpf}`;

  const apagarFacial = async () => {
    try {
      const r = await fetch(`${API}/public/facial/remover`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf }),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success("Cadastro facial apagado.");
        carregarFacial();
      } else {
        toast.error("Não foi possível apagar. Tente novamente.");
      }
    } catch {
      toast.error("Falha de conexão. Tente novamente.");
    }
  };

  const carregar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/meu-perfil?${qs}`);
      const d = await r.json();
      if (d.ok) setPerfil(d.perfil);
      else toast.error("Não foi possível carregar o perfil.");
    } catch { toast.error("Falha de comunicação. Tente novamente."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origem, eql, cpf]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarFacial(); }, [carregarFacial]);

  const abrirEdicao = () => {
    setForm({
      nome: perfil?.nome || "",
      rg: perfil?.rg || "",
      data_nascimento: String(perfil?.data_nascimento || "").slice(0, 10),
      email: perfil?.email || "",
      celular: `${perfil?.ddi_celular || ""}${perfil?.celular || ""}`.replace(/(?!^\+)\D/g, ""),
    });
    setSenhaForm({ nova: "", confirma: "" });
    setErro("");
    setModo("edit");
  };

  const salvar = async () => {
    setErro("");
    if (!form.nome.trim()) { setErro("O nome não pode ficar em branco."); return; }
    if (senhaForm.nova || senhaForm.confirma) {
      if (!senhaForte(senhaForm.nova)) {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
        return;
      }
      if (senhaForm.nova !== senhaForm.confirma) {
        setErro("A confirmação não confere com a nova senha.");
        return;
      }
    }
    setSalvando(true);
    try {
      const payload = {
        origem, eql, cpf,
        nome: form.nome, rg: form.rg, data_nascimento: form.data_nascimento,
        email: form.email, celular: form.celular,
      };
      if (senhaForm.nova) payload.senha = senhaForm.nova;
      const r = await fetch(`${API}/public/meu-perfil`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.ok) {
        toast.success(senhaForm.nova
          ? "Dados e senha atualizados. Use a nova senha no próximo acesso."
          : "Dados atualizados com sucesso.");
        setModo("view");
        carregar();
      } else if (d.motivo === "senha_fraca") {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
      } else if (d.motivo === "dados_invalidos") {
        setErro("Dados inválidos. Verifique as informações preenchidas.");
      } else {
        setErro("Não foi possível salvar. Tente novamente.");
      }
    } catch {
      setErro("Falha de comunicação. Tente novamente.");
    } finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex flex-col" style={{ background: "#11131b", fontFamily: FONT_MAIN }}
      data-testid="appmobile-perfil-overlay">
      <header className="flex items-center gap-3 px-4 py-3.5"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", paddingTop: "max(14px, env(safe-area-inset-top))" }}>
        <button type="button" data-testid="appmobile-perfil-voltar" aria-label="Voltar"
          onClick={() => (modo === "edit" ? setModo("view") : onClose())}
          className="w-10 h-10 flex items-center justify-center rounded-xl active:bg-white/10"
          style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-base font-bold" style={{ color: "#b4c5ff" }} data-testid="appmobile-perfil-titulo">
          {modo === "edit" ? "Alterar Dados" : "Meu Perfil"}
        </h1>
        {modo === "view" && perfil && (
          <button type="button" data-testid="appmobile-perfil-alterar" onClick={abrirEdicao}
            className="ml-auto flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white active:scale-[0.97] transition-transform"
            style={{ background: "#2563EB", boxShadow: "0 0 12px rgba(37,99,235,0.3)" }}>
            <Pencil className="h-3.5 w-3.5" /> Alterar dados
          </button>
        )}
      </header>

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-28">
        {!perfil ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#8d90a0" }} /></div>
        ) : modo === "view" ? (
          <div data-testid="appmobile-perfil-view">
            <div className="text-center mb-4">
              <p className="text-sm font-semibold" style={{ color: "#e1e2ed" }} data-testid="appmobile-perfil-local">{perfil.local_acesso_descricao || "—"}</p>
              <p className="text-xs mt-0.5" style={{ color: "#8d90a0" }} data-testid="appmobile-perfil-eql">{perfil.eql || "—"}</p>
            </div>
            <div className="rounded-xl px-4 py-1 relative overflow-hidden" style={glassCard}>
              <InfoRow label="Matrícula" value={perfil.matricula} testid="appmobile-perfil-matricula" />
              <InfoRow label="Nome" value={perfil.nome} testid="appmobile-perfil-nome" />
              <InfoRow label="CPF" value={fmtCpf(perfil.cpf)} testid="appmobile-perfil-cpf" />
              <InfoRow label="RG" value={perfil.rg} testid="appmobile-perfil-rg" />
              <InfoRow label="Nascimento" value={fmtDataBR(perfil.data_nascimento)} testid="appmobile-perfil-nascimento" />
              <InfoRow label="E-mail" value={perfil.email} testid="appmobile-perfil-email" />
              <InfoRow label="Whatsapp" value={fmtFone(perfil.ddi_celular, perfil.celular)} testid="appmobile-perfil-whatsapp" />
            </div>
            <div className="mt-4 rounded-xl p-4 relative overflow-hidden" style={glassCard}
              data-testid="appmobile-perfil-eqls-adicionais">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#b4c5ff" }}>
                Também listado nas EQL
              </p>
              <p className="text-xs" style={{ color: "#e1e2ed" }} data-testid="appmobile-perfil-eqls-adicionais-lista">
                {(perfil.eqls_adicionais || []).length
                  ? perfil.eqls_adicionais.map((x) => String(x.eql || "").toUpperCase()).filter(Boolean).join(", ")
                  : "Nenhuma EQL adicional"}
              </p>
            </div>
            <div className="mt-4 rounded-xl p-4 relative overflow-hidden" style={glassCard}
              data-testid="appmobile-perfil-facial-secao">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1.5" style={{ color: "#b4c5ff" }}>
                <ScanFace className="h-3.5 w-3.5" /> Reconhecimento Facial (2FA)
              </p>
              {!facial ? (
                <p className="text-xs" style={{ color: "#8d90a0" }}>Carregando...</p>
              ) : !facial.tenant_2fa ? (
                <p className="text-xs" style={{ color: "#8d90a0" }} data-testid="appmobile-perfil-facial-desabilitado">
                  Não habilitado neste condomínio.
                </p>
              ) : (
                <>
                  <p className="text-xs mb-3" style={{ color: facial.enrolled ? "#7ef0a0" : "#f4a261" }}
                    data-testid="appmobile-perfil-facial-status">
                    {facial.enrolled
                      ? "Cadastro facial ativo — necessário para usar o QR Acesso."
                      : "Sem cadastro facial. Cadastre para continuar usando o QR Acesso."}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" data-testid="appmobile-perfil-facial-refazer"
                      onClick={() => setFacialOpen(true)}
                      className="flex-1 h-10 rounded-xl text-xs font-semibold text-white flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
                      style={{ background: "#2563EB", touchAction: "manipulation" }}>
                      <ScanFace className="h-4 w-4" /> {facial.enrolled ? "Refazer foto" : "Cadastrar foto"}
                    </button>
                    {facial.enrolled && (
                      <button type="button" data-testid="appmobile-perfil-facial-apagar" onClick={apagarFacial}
                        className="h-10 px-4 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform"
                        style={{ background: "rgba(239,68,68,0.15)", color: "#ffb4ab", border: "1px solid rgba(239,68,68,0.3)", touchAction: "manipulation" }}>
                        <Trash2 className="h-4 w-4" /> Apagar
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4" data-testid="appmobile-perfil-form">
            <div className="text-center mb-1">
              <p className="text-sm font-semibold" style={{ color: "#e1e2ed" }}>{perfil.local_acesso_descricao || "—"}</p>
              <p className="text-xs mt-0.5" style={{ color: "#8d90a0" }}>{perfil.eql} · Matrícula {perfil.matricula} · CPF {fmtCpf(perfil.cpf)}</p>
            </div>
            <EditField label="Nome" testid="appmobile-perfil-edit-nome" value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome completo" />
            <EditField label="RG" testid="appmobile-perfil-edit-rg" value={form.rg}
              onChange={(e) => setForm({ ...form, rg: e.target.value })} placeholder="Somente números" inputMode="numeric" />
            <EditField label="Nascimento" testid="appmobile-perfil-edit-nascimento" type="date" value={form.data_nascimento}
              onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })} />
            <EditField label="E-mail" testid="appmobile-perfil-edit-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="seu@email.com" />
            <EditField label="Whatsapp" testid="appmobile-perfil-edit-whatsapp" value={form.celular}
              onChange={(e) => setForm({ ...form, celular: e.target.value })} placeholder="+5511999999999" inputMode="tel" />

            <div className="rounded-xl p-4 relative overflow-hidden" style={glassCard} data-testid="appmobile-perfil-senha-secao">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#b4c5ff" }}>Senha</p>
              <p className="text-[11px] mb-3" style={{ color: "#8d90a0" }}>
                Mantenha em branco para conservar a senha atual. Para alterar, preencha e confirme nos dois campos.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  { label: "Nova senha", v: senhaForm.nova, set: (x) => setSenhaForm({ ...senhaForm, nova: x }), show: showN, tgl: () => setShowN(!showN), tid: "appmobile-perfil-edit-senha" },
                  { label: "Confirmar nova senha", v: senhaForm.confirma, set: (x) => setSenhaForm({ ...senhaForm, confirma: x }), show: showC, tgl: () => setShowC(!showC), tid: "appmobile-perfil-edit-senha2" },
                ].map((f) => (
                  <div key={f.tid}>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "#c3c6d7" }}>{f.label}</label>
                    <div className="relative">
                      <input type={f.show ? "text" : "password"} data-testid={f.tid} value={f.v}
                        onChange={(e) => f.set(e.target.value)} placeholder="••••••••"
                        className={`${inputClass} pr-14`} style={inputStyle} />
                      <button type="button" data-testid={`${f.tid}-toggle`} onClick={f.tgl}
                        aria-label={f.show ? "Ocultar senha" : "Mostrar senha"}
                        className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full active:bg-white/10 transition-colors"
                        style={{ color: "#8d90a0", touchAction: "manipulation" }}>
                        {f.show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {erro && (
              <div data-testid="appmobile-perfil-erro"
                className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                style={{ background: "rgba(255, 180, 171, 0.08)", color: "#ffb4ab", border: "1px solid rgba(255, 180, 171, 0.3)" }}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>{erro}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button type="button" data-testid="appmobile-perfil-cancelar" onClick={() => setModo("view")} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-medium active:scale-[0.98] transition-transform"
                style={{ color: "#8d90a0", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
                Cancelar
              </button>
              <button type="button" data-testid="appmobile-perfil-salvar" onClick={salvar} disabled={salvando}
                className="flex-1 py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
                style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
                {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Salvar"}
              </button>
            </div>
          </div>
        )}
      </main>

      {facialOpen && (
        <MobileFacial origem={origem} eql={eql} cpf={cpf} modo="enroll"
          onClose={() => setFacialOpen(false)}
          onSucesso={() => { setFacialOpen(false); carregarFacial(); }} />
      )}
    </div>
  );
}
